"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { extractProperty, getUmbracoMediaUrl, replaceRichTextLinks } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import PowerNumberStat, { PowerNumberStatData } from "./PowerNumberStat"

type Props = {
  data: any
}

const MAX_ITEMS = 4

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 120px;
  }
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1200px;
  color: var(--comp-text);
  text-align: start;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 48px;
  }
`

const Content = styled.div`
  flex: 1;
  min-width: 0;
`

const Header = styled.div`
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    text-align: start;
  }
`

/* Rich-text title (ASH-1556): the editor underlines specific words via the RTE,
   so there's no whole-line border underline. RTE wraps content in <p> — render
   it inline so the title stays on one line. Desktop title stays on one line. */
const Heading = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  p {
    display: inline;
    margin: 0;
  }
  /* Accent words are marked bold in the CMS. Rendered with the brush-underline
     SVG as a CSS mask so the colour follows currentColor (ASH-1549). */
  strong {
    text-decoration: none;
    display: inline-block;
    margin: 0;
    position: relative;
    font-weight: 700;
    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      inset-inline: 0;
      height: 6px;
      z-index: 1;
      background-color: currentColor;
      -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
      mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    white-space: nowrap;
  }
`

const SubtitleText = styled.span`
  font-weight: 700;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-top: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 48px;
  }
`

const StatText = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--inner-text, var(--comp-text));
  margin: 8px 0 0;
`

const ImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 292 / 455;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 292px;
    flex-shrink: 0;
  }
`

// --- Types & parsing ---

type PowerNumbers1Item = PowerNumberStatData & {
  text: string
}

function parseItem(block: any): PowerNumbers1Item {
  return {
    number: (extractProperty(block, "number") as string) || "",
    numberCaption: (extractProperty(block, "numberCaption") as string) || "",
    textLabel: (extractProperty(block, "textLabel") as string) || "",
    text: (extractProperty(block, "text") as string) || "",
  }
}

function isEmptyItem(item: PowerNumbers1Item): boolean {
  return !item.number && !item.numberCaption && !item.textLabel && !item.text
}

// --- Main Component ---

function PowerNumbers1({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale

  const headline = extractProperty(data, "headline") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const image = extractProperty(data, "image", true) as any[] | undefined
  const itemBlocks = extractProperty(data, "items") as any[] | ""
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => !isEmptyItem(item))
    .slice(0, MAX_ITEMS)

  const imageUrl = image?.[0]?.url || ""

  if (!headline && !subtitle && items.length === 0) return null

  return (
    <Section dir={isRtl ? "rtl" : "ltr"}>
      <Panel>
        {/* content first in JSX = right side in RTL */}
        <Content>
          {(headline || subtitle) && (
            <Header>
              <Heading>
                {headline && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(headline, true),
                    }}
                  />
                )}
                {headline && subtitle && " "}
                {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
              </Heading>
            </Header>
          )}
          {items.length > 0 && (
            <StatsGrid>
              {items.map((item, i) => (
                <div key={i}>
                  <PowerNumberStat stat={item} variant="mode1" />
                  {item.text && <StatText>{item.text}</StatText>}
                </div>
              ))}
            </StatsGrid>
          )}
        </Content>
        {imageUrl && (
          <ImageWrapper>
            <Image
              src={getUmbracoMediaUrl(imageUrl)}
              fill
              alt={image?.[0]?.altText || ""}
              style={{ objectFit: "cover" }}
            />
          </ImageWrapper>
        )}
      </Panel>
    </Section>
  )
}

export default PowerNumbers1
